import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {number | undefined} */
  #lastChangedFieldsetIndex;

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.addEventListener('click', this.#handleMelatoninFilterClick);
    this.#initializeMelatoninFilter();
    this.#ensureMatrixSelection();
    this.#resizeObserver.observe(this);
  }

  /**
   * Ensures the matrix picker has an available option selected when the current selection is sold out.
   */
  #ensureMatrixSelection() {
    if (!this.querySelector('.variant-option--matrix-row')) return;

    const checkedInput = this.querySelector('.variant-option--matrix-row:not([hidden]) input:checked');
    if (checkedInput instanceof HTMLInputElement) return;

    const firstVisibleInput = this.querySelector(
      '.variant-option--matrix-row:not([hidden]) .variant-option__row-options input[type="radio"]'
    );

    if (firstVisibleInput instanceof HTMLInputElement) {
      firstVisibleInput.checked = true;
      firstVisibleInput.dataset.currentChecked = 'true';
      firstVisibleInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Initializes melatonin filter buttons and visible matrix rows.
   */
  #initializeMelatoninFilter() {
    const filterGroup = this.querySelector('.variant-picker__melatonin-filter');
    if (!filterGroup) return;

    const initialFilter = filterGroup.dataset.initialFilter || 'regular';
    const resolvedFilter = this.#resolveMelatoninFilter(initialFilter);
    this.#applyMelatoninFilter(resolvedFilter, { selectVisibleVariant: false });
  }

  /**
   * Picks a filter that has visible selectable options.
   * @param {'regular' | 'melatonin-free'} preferredFilter
   * @returns {'regular' | 'melatonin-free'}
   */
  #resolveMelatoninFilter(preferredFilter) {
    if (this.#melatoninFilterHasVisibleOptions(preferredFilter)) return preferredFilter;

    const alternateFilter = preferredFilter === 'regular' ? 'melatonin-free' : 'regular';
    if (this.#melatoninFilterHasVisibleOptions(alternateFilter)) return alternateFilter;

    return preferredFilter;
  }

  /**
   * @param {'regular' | 'melatonin-free'} filter
   * @returns {boolean}
   */
  #melatoninFilterHasVisibleOptions(filter) {
    const filterGroup = this.querySelector('.variant-picker__melatonin-filter');
    const filterMode = filterGroup?.dataset.melatoninFilterMode || 'rows';

    if (filterMode === 'options') {
      return Boolean(
        this.querySelector(
          `.variant-option__matrix-label[data-melatonin-group="${filter}"]:not([hidden]) input[type="radio"]`
        )
      );
    }

    return Boolean(
      this.querySelector(
        `.variant-option--matrix-row[data-melatonin-group="${filter}"]:not([hidden]) input[type="radio"]`
      )
    );
  }

  /**
   * Shows matrix rows for the selected melatonin filter group.
   * @param {'regular' | 'melatonin-free'} filter
   * @param {{ selectVisibleVariant?: boolean }} [options]
   */
  #applyMelatoninFilter(filter, { selectVisibleVariant = true } = {}) {
    const filterGroup = this.querySelector('.variant-picker__melatonin-filter');
    if (!filterGroup) return;

    const filterMode = filterGroup.dataset.melatoninFilterMode || 'rows';

    filterGroup.querySelectorAll('[data-melatonin-filter]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      const isActive = button.dataset.melatoninFilter === filter;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    const rows = this.querySelectorAll('.variant-option--matrix-row');
    let hasVisibleSelection = false;

    if (filterMode === 'options') {
      rows.forEach((row) => {
        if (!(row instanceof HTMLElement)) return;

        row.hidden = false;
        row.classList.remove('variant-option--matrix-row--hidden');

        let visibleOptionCount = 0;

        row.querySelectorAll('.variant-option__matrix-label').forEach((label) => {
          if (!(label instanceof HTMLElement)) return;

          const shouldShow = label.dataset.melatoninGroup === filter;
          label.classList.toggle('variant-option__matrix-label--hidden', !shouldShow);
          label.toggleAttribute('hidden', !shouldShow);

          if (shouldShow) {
            visibleOptionCount += 1;
            if (label.querySelector('input:checked')) {
              hasVisibleSelection = true;
            }
          }
        });

        const hideRow = visibleOptionCount === 0;
        row.classList.toggle('variant-option--matrix-row--hidden', hideRow);
        row.toggleAttribute('hidden', hideRow);
      });
    } else {
      rows.forEach((row) => {
        if (!(row instanceof HTMLElement)) return;

        const shouldShow = row.dataset.melatoninGroup === filter;
        row.classList.toggle('variant-option--matrix-row--hidden', !shouldShow);
        row.toggleAttribute('hidden', !shouldShow);

        if (shouldShow && row.querySelector('input:checked')) {
          hasVisibleSelection = true;
        }
      });
    }

    if (selectVisibleVariant && !hasVisibleSelection) {
      const firstVisibleInput = this.querySelector(
        filterMode === 'options'
          ? `.variant-option__matrix-label[data-melatonin-group="${filter}"]:not([hidden]) input[type="radio"]`
          : `.variant-option--matrix-row[data-melatonin-group="${filter}"]:not([hidden]) input[type="radio"]`
      );

      if (firstVisibleInput instanceof HTMLInputElement) {
        firstVisibleInput.checked = true;
        firstVisibleInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  /**
   * Switches melatonin filter when a filter button is clicked.
   * @param {Event} event
   */
  #handleMelatoninFilterClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('[data-melatonin-filter]');
    if (!button || !this.contains(button)) return;

    const filter = button.dataset.melatoninFilter;
    if (filter !== 'regular' && filter !== 'melatonin-free') return;

    event.preventDefault();
    this.#applyMelatoninFilter(filter);
  };

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    // Track which fieldset the user just changed so updateVariantPicker can
    // preserve the other fieldsets' selections after morphing the server response.
    if (event.target instanceof HTMLInputElement) {
      this.#lastChangedFieldsetIndex = Number(event.target.dataset.fieldsetIndex);
    }

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    const variantId = selectedOption.dataset.variantId;
    if (variantId && !source) {
      params.push(`variant=${variantId}`);
    } else {
      try {
        if (this.selectedOptionsValues.length && !source) {
          params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
        } else if (source === 'product-card') {
          if (sourceSelectedOptionsValues.length) {
            params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
          } else {
            params.push(`option_values=${selectedOption.dataset.optionValueId}`);
          }
        }
      } catch (error) {
        console.error(error);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    if (this.dataset.templateProductMatch === 'true' && this.dataset.sectionId) {
      params.push(`section_id=${this.dataset.sectionId}`);
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const newProduct = this.updateVariantPicker(html);
          const variant = JSON.parse(textContent);
          const sourceId = this.#getSelectedOptionId() ?? String(variant?.id ?? '');

          if (sourceId || variant?.id) {
            this.dispatchEvent(
              new VariantUpdateEvent(variant, sourceId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    // Snapshot which option value ID is checked in each fieldset before the
    // morph. When the selected combination isn't a valid variant, Shopify falls
    // back to selected_or_first_available_variant, which can override options
    // the user explicitly chose (e.g. reverting Package Type from "Bucket" to
    // "Foil Bag" when the user only clicked a weight).
    const currentFieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const preSelectedIds = currentFieldsets.map((fieldset, index) => {
      // Don't snapshot the fieldset the user just changed - let the server
      // decide that value (it already reflects the user's click).
      if (index === this.#lastChangedFieldsetIndex) return undefined;
      const checked = fieldset.querySelector('input:checked');
      return checked instanceof HTMLInputElement ? checked.dataset.optionValueId : undefined;
    });
    this.#lastChangedFieldsetIndex = undefined;

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });

    // Re-assert the user's intended selections for any fieldset where the
    // server's fallback variant overrode their explicit choice.
    for (const optionValueId of preSelectedIds) {
      if (!optionValueId) continue;
      const input = this.querySelector(`input[data-option-value-id="${CSS.escape(optionValueId)}"]`);
      if (input instanceof HTMLInputElement && !input.checked) {
        input.checked = true;
      }
    }

    // Rebuild internal tracking so #radios and #checkedIndices reference the
    // post-morph DOM nodes and reflect the corrected checked states.
    this.#reinitializeState();

    this.#initializeMelatoninFilter();
    this.updateVariantPickerCss();

    return newProduct;
  }

  /**
   * Rebuilds #radios and #checkedIndices from the live DOM.
   * Must be called after any morph that replaces fieldset child nodes.
   */
  #reinitializeState() {
    this.#radios = [];
    this.#checkedIndices = [];
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset.querySelectorAll('input') ?? []);
      this.#radios.push(radios);
      const checkedIndex = radios.findIndex((radio) => radio.checked);
      this.#checkedIndices.push(checkedIndex !== -1 ? [checkedIndex] : []);
    });
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID without throwing.
   * @returns {string | undefined}
   */
  #getSelectedOptionId() {
    try {
      return this.selectedOptionId;
    } catch (error) {
      console.warn(error);
      return undefined;
    }
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
    const matrixRowCount = this.querySelectorAll('.variant-option--matrix-row').length;

    return selectedOptions.flatMap((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      const ids = [];
      if (option instanceof HTMLInputElement) {
        const fieldset = option.closest('fieldset.variant-option--matrix-row[data-option-value-id]');
        if (
          matrixRowCount > 1 &&
          fieldset instanceof HTMLElement &&
          fieldset.dataset.optionValueId
        ) {
          ids.push(fieldset.dataset.optionValueId);
        }
      }
      ids.push(optionValueId);

      return ids;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
